/**
 * Mục "Trợ lý Studio" của bản kê lương (QĐ 0164).
 *
 * Tách khỏi `WorkBreakdownDetail.tsx` vì tệp đó đã chạm trần 300 dòng của
 * lint — không phải vì nội dung khác loại. Cái rào giữ cho mục "Lễ tân" không
 * vẽ cho phòng này (`hasOwnHoursSection`) nằm ở `deptSections.ts`, cạnh rào
 * của mục thợ.
 */

import type { PayrollRecord } from "../../lib";
import { vnd } from "../../constants";
import { AmountRow, AmountTable, DetailSection } from "./primitives";

/**
 * Giờ dạng "19,5 giờ". Dấu phẩy thập phân, không phải dấu chấm.
 *
 * Để cục bộ ở đây vì đang là chỗ duy nhất cần; có chỗ thứ hai thì dời sang
 * `features/payroll/constants.ts` cạnh `vnd`.
 */
function gio(value: number): string {
  return `${value.toLocaleString("vi-VN", { maximumFractionDigits: 2 })} giờ`;
}

/**
 * Trợ lý Studio — trả theo giờ ca đã duyệt (QĐ 0164).
 *
 * 🔴 In `approvedHours` chứ KHÔNG in `record.hoursWorked`. Hai ô nói hai
 * chuyện: `hoursWorked` là số nguyên máy chủ làm tròn ĐÚNG MỘT LẦN rồi giữ
 * trên bản ghi (nó còn được cộng dồn khi một người kiêm hai vai —
 * `core/payroll/motivation_view.go`), còn TIỀN thì nhân trên giờ lẻ thật
 * (`RoundVND(hours × đơn giá)` ở `pkg/engines/payroll`). 19,5 giờ mà in thành
 * "20 giờ" cạnh số tiền là mời người đọc nhân 20 × đơn giá rồi kết luận lương
 * tính sai.
 *
 * Giờ tăng ca thì ngược lại — máy tính nó TỪ con số đã làm tròn. Đừng chỉnh
 * cho "nhất quán" ở đây: màn hiển thị phải nói đúng cái máy đã làm.
 */
export function StudioAssistantDetail({ record }: { record: PayrollRecord }) {
  const b = record.studioAssistantBreakdown;
  if (!b) return null;

  const dieuChinh = b.hourlyRateAdjustment ?? 0;
  // Dấu đi LIỀN con số, không tách thành chữ "+ ... 5.000đ (5.000đ)": in số
  // điều chỉnh hai lần thì người đọc phải tự đoán hai số đó có phải một hay
  // không. `vnd` tự mang dấu trừ, nên chỉ cần thêm dấu cộng cho số dương.
  const goiDonGia =
    dieuChinh === 0
      ? "Mức chung của phòng"
      : `Mức chung ${vnd(b.commonHourlyRate)}, điều chỉnh riêng ${dieuChinh > 0 ? "+" : ""}${vnd(dieuChinh)}`;

  const canhBao = b.rateNotPositive
    ? "Đơn giá giờ đang bằng 0 nên phần tiền giờ chưa được cộng. Nhắn kế toán kiểm lại ô đơn giá của bạn."
    : b.noApprovedShifts
      ? "Tháng này chưa có ca nào được duyệt, nên chưa có tiền giờ. Ca đã đăng ký phải được ký duyệt mới tính."
      : null;

  return (
    <DetailSection
      testId="detail-studio-assistant"
      icon="🧰"
      title="Trợ lý Studio"
      countLabel={`${b.shiftCount} ca`}
      total={(b.hourlyPay ?? 0) + (b.overtimePay ?? 0)}
      note={
        canhBao ? (
          <span data-testid="tro-ly-canh-bao" className="font-medium">
            {canhBao}
          </span>
        ) : undefined
      }
    >
      <AmountTable>
        <AmountRow
          testId="tro-ly-gio"
          label="Giờ ca đã duyệt"
          hint={`Cộng từ ${b.shiftCount} ca được ký duyệt trong tháng`}
          value={gio(b.approvedHours ?? 0)}
        />
        <AmountRow
          testId="tro-ly-don-gia"
          label="Đơn giá một giờ"
          hint={goiDonGia}
          value={b.hourlyRate}
        />
        <AmountRow label="Tiền giờ" hint={`${gio(b.approvedHours ?? 0)} × đơn giá`} value={b.hourlyPay} />
        {(b.overtimeHours ?? 0) > 0 ? (
          <AmountRow
            testId="tro-ly-tang-ca"
            label="Giờ tăng ca"
            hint="Phần vượt chuẩn tháng, tính theo giờ đã làm tròn"
            value={gio(b.overtimeHours ?? 0)}
          />
        ) : null}
        {(b.overtimePay ?? 0) > 0 ? (
          <AmountRow label="Tiền tăng ca" value={b.overtimePay} />
        ) : null}
      </AmountTable>
      <DnJobRows b={b} />
    </DetailSection>
  );
}

/**
 * Phụ việc job doanh nghiệp (QĐ 0178 §5.7a) — nguồn giờ thứ hai, mức riêng. Máy
 * cộng nó vào ô Hoa hồng của bản ghi (để không thành giờ tăng ca), nên phiếu
 * ghi "Tiền job doanh nghiệp" chứ không gọi là hoa hồng. Từng buổi job nằm ở
 * mục "Khách đã làm" phía dưới.
 */
function DnJobRows({ b }: { b: NonNullable<PayrollRecord["studioAssistantBreakdown"]> }) {
  const minutes = b.dnJobMinutes ?? 0;
  const pay = b.dnJobPay ?? 0;
  if (!minutes && !pay && !b.dnJobHoursMissing && !b.dnJobRateNotSet) return null;
  const warn = b.dnJobRateNotSet
    ? "Mức giờ job doanh nghiệp chưa đặt nên tiền job đang 0đ. Nhắn kế toán kiểm thẻ Trợ lý Studio."
    : b.dnJobHoursMissing
      ? "Có buổi job chưa ghi số phút — tiền job đang tạm tính, đủ sổ công mới chốt."
      : null;
  return (
    <div data-testid="tro-ly-job-dn" className="mt-3">
      <h4 className="mb-1 text-[15px] font-semibold">Phụ việc job doanh nghiệp</h4>
      {warn ? (
        <p role="alert" className="mb-2 text-sm font-medium">
          {warn}
        </p>
      ) : null}
      <AmountTable>
        <AmountRow label="Giờ job đã ghi" value={gio(minutes / 60)} />
        <AmountRow label="Mức một giờ job" value={b.dnJobHourlyRate ?? 0} />
        <AmountRow label="Tiền job doanh nghiệp" hint="Số phút × mức giờ ÷ 60" value={pay} />
      </AmountTable>
    </div>
  );
}
